/**
 * Wave 42 · Founder Wall of Fame — public page listing all 100 slot claimers.
 * Route: /atc/founders
 */
import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import './atc.css';

export default function FounderWall() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    let cancel = false;
    api.get('/atc/launch-party/wall').then((r) => { if (!cancel) setData(r.data); }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.founders;
    const ql = q.trim().toLowerCase();
    return data.founders.filter((f) => (f.user_username || '').toLowerCase().includes(ql)
      || String(f.slot_number).includes(ql));
  }, [data, q]);

  if (!data) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">⏳ Loading Wall…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-amber-950 to-slate-950 text-white p-4 sm:p-8 relative overflow-hidden"
         data-testid="atc-founder-wall">
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="text-center mb-6">
          <div className="inline-block px-3 py-1 rounded-full bg-amber-500/30 border border-amber-400/60 text-[10px] font-black uppercase tracking-widest mb-3">
            🏛️ WALL OF FAME
          </div>
          <h1 className="text-4xl sm:text-6xl font-black mb-2 bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-400 bg-clip-text text-transparent">
            Anant Trade Center · Founders
          </h1>
          <p className="text-sm sm:text-base opacity-80">
            Bharat's first {data.count} community founders. Har slot forever engraved. Shukriya Bhai! 🙏
          </p>
        </div>

        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="rounded-xl bg-slate-950/60 border border-amber-400/40 px-4 py-2">
            <span className="text-3xl font-black text-amber-200" data-testid="atc-wall-count">{data.count}</span>
            <span className="text-sm opacity-70"> / {data.slots_total} founders</span>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="atc-wall-search"
                 placeholder="🔎 Search by @username or slot #"
                 className="flex-1 min-w-[200px] max-w-md px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm" />
          <a href="/atc/launch-party" data-testid="atc-wall-claim-cta"
             className="px-4 py-2 rounded-xl bg-gradient-to-r from-fuchsia-400 to-amber-400 text-slate-900 text-sm font-black hover:brightness-110">
            🎁 Get Your Slot
          </a>
        </div>

        {data.top_referrers?.length > 0 && (
          <div className="rounded-xl bg-slate-950/60 border border-emerald-400/30 p-3 mb-4" data-testid="atc-wall-top-refs">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-2">🎯 Top Referrers</div>
            <div className="flex gap-2 flex-wrap">
              {data.top_referrers.map((r, i) => (
                <span key={r.username} className="px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-[11px] font-black">
                  {['🥇','🥈','🥉','🏅','🏅'][i]} @{r.username} · {r.count} invite{r.count !== 1 ? 's' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="text-center py-8 text-xs opacity-60 rounded-xl border border-dashed border-amber-400/30">
            Koi founder nahi mila. Try a different search.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filtered.map((f) => (
              <div key={f.slot_number} data-testid={`atc-wall-slot-${f.slot_number}`}
                   className={`relative rounded-xl p-3 text-center border-2 ${
                     f.slot_number === 1 ? 'bg-gradient-to-br from-amber-500/40 to-yellow-600/20 border-amber-300 shadow-lg shadow-amber-500/30 animate-pulse' :
                     f.slot_number <= 3 ? 'bg-slate-900/60 border-amber-400/50' :
                     f.slot_number <= 10 ? 'bg-slate-900/60 border-slate-400/40' :
                     'bg-slate-900/40 border-slate-700'
                   }`}>
                <div className="text-2xl mb-1">
                  {f.slot_number === 1 ? '👑' : f.slot_number <= 3 ? '🏆' : f.slot_number <= 10 ? '🥇' : '⭐'}
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest opacity-70">SLOT</div>
                <div className="text-2xl font-black text-amber-200">#{f.slot_number}</div>
                <div className="text-xs font-black truncate mt-1">@{f.user_username || 'founder'}</div>
                <div className="text-[9px] opacity-70">{new Date(f.created_at).toLocaleDateString()}</div>
                {f.is_double_founder && (
                  <span data-testid={`atc-wall-chain-${f.slot_number}`}
                        className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded bg-emerald-500 text-slate-900 text-[9px] font-black animate-pulse">
                    🔗 ×{f.referrals_made}
                  </span>
                )}
                {f.referred_by_username && (
                  <div className="text-[9px] opacity-60 mt-0.5">via @{f.referred_by_username}</div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 text-center text-[10px] opacity-60">
          Har founder ka naam hamesha yahan rahega · Anant Trade Center 2026
        </div>
      </div>
    </div>
  );
}
